// Minimal history-based router: the app screens, plus the public start page and sign-in.
import { useSyncExternalStore } from 'react';

export type Route =
  | { name: 'patients' }
  | { name: 'patient'; id: string }
  | { name: 'offers' }
  | { name: 'reports' }
  /** `id` is 'new' for a new offer. */
  | { name: 'offer'; id: string }
  | { name: 'settings' }
  | { name: 'profile' }
  | { name: 'login' };

const EVENT = 'dentlog:navigate';

export function parseRoute(pathname: string): Route {
  const patient = /^\/patients\/([0-9a-f-]{36})\/?$/i.exec(pathname);
  if (patient) return { name: 'patient', id: patient[1] };
  const offer = /^\/offers\/([0-9a-f-]{36}|new)\/?$/i.exec(pathname);
  if (offer) return { name: 'offer', id: offer[1] };
  if (/^\/offers\/?$/.test(pathname)) return { name: 'offers' };
  if (/^\/reports\/?$/.test(pathname)) return { name: 'reports' };
  // /admin and /audit were the old clinic and audit pages; both live in Settings now.
  if (/^\/(settings|admin|audit)\b/.test(pathname)) return { name: 'settings' };
  if (pathname.startsWith('/profile')) return { name: 'profile' };
  if (pathname.startsWith('/login')) return { name: 'login' };
  return { name: 'patients' };
}

export function pathFor(route: Route): string {
  switch (route.name) {
    case 'patient':
      return `/patients/${route.id}`;
    case 'offers':
      return '/offers';
    case 'reports':
      return '/reports';
    case 'offer':
      return `/offers/${route.id}`;
    case 'settings':
      return '/settings';
    case 'profile':
      return '/profile';
    case 'login':
      return '/login';
    default:
      return '/';
  }
}

export function navigate(route: Route, replace = false) {
  const path = pathFor(route);
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  window.dispatchEvent(new Event(EVENT));
  window.scrollTo({ top: 0 });
}

function subscribe(cb: () => void) {
  window.addEventListener('popstate', cb);
  window.addEventListener(EVENT, cb);
  return () => {
    window.removeEventListener('popstate', cb);
    window.removeEventListener(EVENT, cb);
  };
}

export function useRoute(): Route {
  const path = useSyncExternalStore(subscribe, () => window.location.pathname);
  return parseRoute(path);
}
